import React from 'react';
import { Card } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { CheckCircle2, XCircle, Ruler, Maximize } from 'lucide-react';

export default function LogoGuidelines() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Logo Guidelines
        </h1>
        <p className="text-base text-body leading-relaxed">
          Our logo is the cornerstone of our visual identity. Use it consistently
          to build brand recognition and maintain professionalism.
        </p>
      </div>

      <Card className="p-8 bg-surface">
        <h2 className="text-2xl font-heading font-semibold text-body mb-6">
          Primary Logo
        </h2>
        <div className="bg-background p-12 rounded-lg flex items-center justify-center">
          <img src="/logo-blue.png" alt="Jackstone Logo" className="h-24" />
        </div>
        <p className="text-sm text-subtle mt-4">
          The primary Jackstone logo features bold, uppercase letterforms designed for maximum
          impact and recognition. Use this version on light backgrounds.
        </p>
      </Card>

      <Card className="p-8 bg-navy">
        <h2 className="text-2xl font-heading font-semibold text-white mb-6">
          Secondary Logo (White Version)
        </h2>
        <div className="bg-navy/50 p-12 rounded-lg flex items-center justify-center">
          <img src="/logo-white.png" alt="Jackstone Logo White" className="h-24" />
        </div>
        <p className="text-sm text-white/80 mt-4">
          The white version of the logo is used on dark backgrounds, photography, and colored surfaces.
        </p>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
            Primary Logo Usage
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div className="text-sm text-subtle">
                Use on white or light backgrounds for maximum visibility
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div className="text-sm text-subtle">
                Maintain brand color (#39678F) whenever possible
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div className="text-sm text-subtle">
                Ensure adequate clear space around the logo
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
            Secondary Logo Usage
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div className="text-sm text-subtle">
                White version for dark backgrounds and photography
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div className="text-sm text-subtle">
                Navy version (#33424F) for subtle applications
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div className="text-sm text-subtle">
                Single-color versions for print constraints
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-body mb-4 flex items-center gap-2">
          <Ruler className="h-5 w-5" />
          Clear Space Rules
        </h3>
        <p className="text-body mb-4">
          Maintain a clear space around the logo equal to the height of the letter 'J' on all sides.
          This ensures the logo remains visible and impactful.
        </p>
        <div className="bg-surface p-8 rounded-lg flex justify-center">
          <div className="border-2 border-dashed border-jackstone-blue/30 p-8 inline-block">
            <img src="/logo-blue.png" alt="Jackstone Logo" className="h-16" />
          </div>
        </div>
        <p className="text-xs text-subtle mt-3">
          Clear space visualization (dashed border represents minimum clearance)
        </p>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-body mb-4 flex items-center gap-2">
          <Maximize className="h-5 w-5" />
          Minimum Size Requirements
        </h3>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-body mb-2">Digital Applications:</p>
            <p className="text-sm text-subtle">Minimum width of 120px for web and digital displays</p>
          </div>
          <div>
            <p className="text-sm font-medium text-body mb-2">Print Applications:</p>
            <p className="text-sm text-subtle">Minimum width of 1 inch (25.4mm) for printed materials</p>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-destructive/50 bg-destructive/5">
        <h3 className="text-xl font-heading font-semibold text-body mb-4 flex items-center gap-2">
          <XCircle className="h-5 w-5 text-destructive" />
          Incorrect Logo Usage
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-4 bg-background rounded border border-destructive/20 flex flex-col items-center">
            <img src="/logo-blue.png" alt="Distorted logo" className="h-12 transform scale-y-150" />
            <p className="text-xs text-destructive mt-3 text-center">Do not stretch or distort</p>
          </div>
          <div className="p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded flex flex-col items-center">
            <img src="/logo-white.png" alt="Wrong color logo" className="h-12" />
            <p className="text-xs text-white mt-3 text-center">Do not use unapproved colours</p>
          </div>
          <div className="p-4 bg-background rounded border border-destructive/20 flex flex-col items-center">
            <img src="/logo-blue.png" alt="Rotated logo" className="h-12 transform rotate-12" />
            <p className="text-xs text-destructive mt-3 text-center">Do not rotate or angle</p>
          </div>
          <div className="p-4 bg-background rounded border border-destructive/20 flex flex-col items-center">
            <img src="/logo-blue.png" alt="Logo with effects" className="h-12 drop-shadow-2xl" />
            <p className="text-xs text-destructive mt-3 text-center">Do not add effects or shadows</p>
          </div>
        </div>
      </Card>

      <Alert>
        <AlertDescription className="text-sm">
          <strong>Logo Files:</strong> Vector files (SVG, PDF) should be used whenever possible.
          PNG files with transparent backgrounds are acceptable for digital applications.
          Always use official logo files provided by the brand team.
        </AlertDescription>
      </Alert>
    </div>
  );
}
